import { Component, OnInit } from '@angular/core';
import { IPageResult } from '../../interfaces/page-result';
import { EngineiusAreaService, EngineiusRegionService, TaggingService } from '../../services';
import { LazyLoadEvent, MessageService, SelectItem, Table } from 'primeng';
import {
  ISupplierAvailabilityDashboard,
  ISupplierAvailabilityDashboardDayDetail,
} from '../../interfaces/supplier-availability-dashboard/supplier-availability-dashboard';
import { ISupplierAvailabilityDashboardFilter } from '../../interfaces/supplier-availability-dashboard/supplier-availability-dashboard-filter';
import { SupplierAvailabilityDashboardService } from '../../services/supplier-availability-dashboard.service';
import { ITagEntity } from '../../interfaces/tag-entity';
import { Message } from '../../classes/Message';
import FileSaver from 'file-saver';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SupplierAvailabilityStateEnum } from '../../interfaces/supplier-schedule';
import { SupplierAvailabilityStateFilter } from '../../enums/supplier-availability-state-filter.enum';

@Component({
  selector: 'app-supplier-availability-dashboard',
  templateUrl: './supplier-availability-dashboard.component.html',
  styleUrls: ['./supplier-availability-dashboard.component.css'],
})
export class SupplierAvailabilityDashboardComponent implements OnInit {
  private readonly itemsPerPageDef = 10;

  public AvailabilityStateEnum = SupplierAvailabilityStateEnum;

  public filterVal: ISupplierAvailabilityDashboardFilter;
  public filter: Subject<ISupplierAvailabilityDashboardFilter>;

  public isLoaded: boolean = false;
  public isLoading: boolean = false;
  public isDownloading: boolean = false;

  public page: IPageResult<ISupplierAvailabilityDashboard> = {
    items: [],
    total: 0,
  };

  public gridDates: Date[];

  public lastJobSearchPeriodOptions: SelectItem[] = [];
  public regionOptions: SelectItem[] = [];
  public areaOptions: SelectItem[] = [];
  public suppliers: SelectItem[] = [];
  public tags: SelectItem[] = [];
  public classifications: SelectItem[] = [];
  public availabilityStateOptions: SelectItem[] = [];

  public defaultDateString: string = '0001-01-01T00:00:00';

  protected readonly SupplierAvailabilityStateFilter = SupplierAvailabilityStateFilter;

  constructor(
    private supplierAvailabilityDashboardService: SupplierAvailabilityDashboardService,
    private areaService: EngineiusAreaService,
    private regionService: EngineiusRegionService,
    private tagService: TaggingService,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    this.gridDates = this.generateGridDates();

    this.initDefaultFilters();

    this.filter = new Subject<ISupplierAvailabilityDashboardFilter>();

    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.load(f);
      this.filterVal = f;
    });

    this.filter.next(this.filterVal);

    this.loadSelectOptions();
  }

  loadSelectOptions(): void {
    this.classifications.push({ label: 'New', value: 'New' });
    this.classifications.push({ label: 'Operational', value: 'Operational' });
    this.classifications.push({ label: 'Plated', value: 'Plated' });
    this.classifications.push({ label: 'Transporter', value: 'Transporter' });

    this.lastJobSearchPeriodOptions.push({ label: 'Show All', value: 1 });
    this.lastJobSearchPeriodOptions.push({ label: 'Last 2 Days', value: 2 });
    this.lastJobSearchPeriodOptions.push({ label: 'Last 3 Days', value: 3 });
    this.lastJobSearchPeriodOptions.push({ label: 'Last 4 Days', value: 4 });
    this.lastJobSearchPeriodOptions.push({ label: 'Last 5 Days', value: 5 });
    this.lastJobSearchPeriodOptions.push({ label: 'Last Week', value: 6 });
    this.lastJobSearchPeriodOptions.push({ label: 'Last 2 Weeks', value: 7 });

    this.availabilityStateOptions.push({
      label: 'Available',
      value: SupplierAvailabilityStateFilter.Available,
    });
    this.availabilityStateOptions.push({
      label: 'Spare Capacity',
      value: SupplierAvailabilityStateFilter.SpareCapacity,
    });

    this.loadEngineiusRegions().then((regions) => (this.regionOptions = regions));
    this.loadEngineiusAreas().then((areas) => (this.areaOptions = areas));
    this.loadSupplierDropdownItems().then((suppliers) => (this.suppliers = suppliers));
    this.loadTagItems().then((tags) => (this.tags = tags));
  }

  loadEngineiusAreas(): Promise<SelectItem[]> {
    return this.areaService.getAll().then((areas) => {
      let areaList = [];
      if (areas != null) {
        areas.forEach((area) => {
          areaList.push({ label: area.name, value: area.id });
        });
      }
      return areaList;
    });
  }

  loadEngineiusRegions(filterRegionsByArea: boolean = false): Promise<SelectItem[]> {
    return this.regionService.getAll().then((regions) => {
      let regionList = [];
      if (regions != null) {
        if (filterRegionsByArea) {
          const filterAreaIds = this.filterVal.filters.areas.value;
          if (filterAreaIds.length > 0) {
            regions = regions.filter((region) => filterAreaIds.includes(region.engineiusAreaId));
          }
        }
        regions.sort((a, b) => a.name.localeCompare(b.name));
        regions.forEach((region) => {
          regionList.push({ label: region.name, value: region.id });
        });
      }
      return regionList;
    });
  }

  loadSupplierDropdownItems(): Promise<SelectItem[]> {
    return this.supplierAvailabilityDashboardService.getSupplierDropdownItems();
  }

  loadTagItems(): Promise<SelectItem[]> {
    return this.tagService
      .getAllTags()
      .toPromise()
      .then((tags) => {
        let tagList = [];
        if (tags != null) {
          tags.forEach((tag) => {
            tagList.push({ label: tag.name, value: tag.id });
          });
        }
        return tagList;
      });
  }

  generateGridDates(): Date[] {
    const dates: Date[] = [];

    let currentDate = this.filterVal?.filters?.fromDate?.value
      ? new Date(this.filterVal.filters.fromDate.value)
      : new Date();

    while (currentDate.getDay() === 6 || currentDate.getDay() === 0) {
      currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
    }

    dates.push(new Date(currentDate));

    for (let i = 1; i < 3; i++) {
      currentDate.setDate(currentDate.getDate() + 1);

      while (currentDate.getDay() === 6 || currentDate.getDay() === 0) {
        currentDate.setDate(currentDate.getDate() + 1);
      }

      dates.push(new Date(currentDate));
    }

    return dates;
  }

  initDefaultFilters() {
    this.filterVal = <ISupplierAvailabilityDashboardFilter>{
      first: 0,
      rows: this.itemsPerPageDef,
      sortField: 'SupplierName',
      sortOrder: 1,
      filters: {
        fromDate: {},
        suppliers: { value: null },
        classifications: { value: null },
        regions: { value: null },
        areas: { value: null },
        tags: { value: null },
        lastJobSearchPeriod: {},
        day1Availability: { value: null },
        day2Availability: { value: null },
        day3Availability: { value: null },
      },
    };
  }

  resetFilters(dt: Table): void {
    dt.sortField = '';
    dt.rows = 10;
    this.initDefaultFilters();
    this.filter.next(this.filterVal);

    this.loadEngineiusRegions(false).then((regions) => (this.regionOptions = regions));

    this.gridDates = this.generateGridDates();
  }

  load(filterCommand: ISupplierAvailabilityDashboardFilter) {
    this.isLoading = true;
    this.isLoaded = false;
    this.gridDates = this.generateGridDates();
    this.supplierAvailabilityDashboardService
      .getSupplierAvailability(filterCommand)
      .then((result) => {
        this.page.items = result.items;
        this.page.total = result.total;
        this.isLoaded = true;
        this.isLoading = false;
      });
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;
    this.filter.next(this.filterVal);
  }

  search(): void {
    this.filterVal.first = 0;
    this.filter.next(this.filterVal);
  }

  selectTag(selectedTag: ITagEntity): void {
    this.filterVal.filters.tags.value = null;
    this.filterVal.first = 0;

    this.filterVal.filters.tags = {
      value: [selectedTag.id],
    };

    this.filter.next(this.filterVal);
  }

  navigateToSupplier(supplierId: number): void {
    const url = `app/supplier-information/${supplierId}`;
    window.open(url, '_blank');
  }

  downloadReport(): void {
    this.isDownloading = true;
    this.supplierAvailabilityDashboardService.downloadReport(this.filterVal).subscribe(
      (x) => this.handleDownloadFile(x, 'SupplierAvailabilityReport.csv'),
      (error) => {
        this.messageService.add(
          Message.Error('An error occurred when attempting to download the requested report.'),
        );
        this.isDownloading = false;
      },
      () => {
        this.messageService.add(
          Message.Success('Download successful. Check your download folder.'),
        );
        this.isDownloading = false;
      },
    );
  }

  private handleDownloadFile(blob, fileName) {
    FileSaver.saveAs(blob, fileName);
  }

  isDefaultDate(dateString: string): boolean {
    return dateString === this.defaultDateString;
  }

  regionSelected(): void {
    this.loadEngineiusRegions(true).then((regions) => (this.regionOptions = regions));
  }

  updateAvailabilityStatus(
    supplier: ISupplierAvailabilityDashboard,
    dayDetail: ISupplierAvailabilityDashboardDayDetail,
  ): void {
    /* No longer disable for DND
    // DND state cannot be changed in this component
    if (dayDetail.availabilityState == SupplierAvailabilityStateEnum.doNotDisturb) {
      return;
    }*/

    const nextState = this.getNextAvailabilityStatus(dayDetail.availabilityState);

    this.supplierAvailabilityDashboardService
      .updateSupplierAvailability(
        supplier.supplierId,
        dayDetail.availabilityState,
        nextState,
        dayDetail.reportingDate,
      )
      .then(() => {
        dayDetail.availabilityState = nextState;
      });
  }

  getNextAvailabilityStatus(status: SupplierAvailabilityStateEnum): number {
    // because a user cannot change a status of DND, we need to skip over that in the transition
    const transitions = {
      [SupplierAvailabilityStateEnum.unset]: SupplierAvailabilityStateEnum.available,
      [SupplierAvailabilityStateEnum.available]: SupplierAvailabilityStateEnum.unavailable,
      [SupplierAvailabilityStateEnum.unavailable]: SupplierAvailabilityStateEnum.doNotDisturb,
      [SupplierAvailabilityStateEnum.doNotDisturb]: SupplierAvailabilityStateEnum.unset,
    };
    return transitions[status];
  }
}
